import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useLoadScript, MarkerF, InfoWindowF, MarkerClustererF } from '@react-google-maps/api';
import { Box, Typography, Link, Paper, Button, Chip } from '@mui/material';
import { styled } from '@mui/system';

// Para tu clave de API de Google Maps, NO la pongas directamente aquí.
// Usa una variable de entorno en tu archivo .env: REACT_APP_Maps_API_KEY=...
const googleMapsApiKey = process.env.REACT_APP_Maps_API_KEY;

const containerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
};

const center = {
  lat: 18.7357, // Centro aproximado de República Dominicana
  lng: -70.1627,
};

const libraries = ["places"];

// Estilo para la tarjeta de detalles flotante al HOVER
const FloatingDetailsCard = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  padding: theme.spacing(2),
  zIndex: 1000,
  maxWidth: '300px',
  boxShadow: theme.shadows[8],
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    left: theme.spacing(1),
    maxWidth: 'calc(100% - 16px)',
  },
}));

// Se pasa `onSpaceSelect` como prop para que el padre (`App.js`) pueda saber qué marcador fue clicado
// `selectedSpaceFromApp` se usa para que el InfoWindow del mapa sepa qué mostrar si es necesario
function PublicMapComponent({ espacios, onSpaceSelect, selectedSpaceFromApp, onBoundsChange, onAddToCart, cart = [], mapsApiKey: mapsApiKeyProp }) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: mapsApiKeyProp || googleMapsApiKey, 
    libraries: libraries,
  });

  const [hoveredSpace, setHoveredSpace] = useState(null);

  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: true,
    mapTypeControl: true,
    fullscreenControl: true,
    styles: [ 
      {
        featureType: "poi",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "transit",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ color: "#f5f5f5" }]
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#cccccc" }]
      }
    ]
  };

  const handleMarkerClick = (space) => {
    // Al hacer clic, notifica al componente padre (App.js)
    // para que el panel lateral de detalles se actualice.
    onSpaceSelect(space);
  };

  const handleInfoWindowClose = () => {
    // Cuando el InfoWindow se cierra, deselecciona el espacio.
    // Esto limpia tanto el InfoWindow como el panel lateral.
    onSpaceSelect(null);
  };

  const [map, setMap] = useState(null);
  
  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const handleIdle = () => {
    if (map && onBoundsChange) {
      const bounds = map.getBounds();
      if (bounds) {
        onBoundsChange(bounds);
      }
    }
  };

  // Emoji por tipo de espacio
  const TIPO_EMOJI = {
    'Valla':             '🪧',
    'Valla Digital':     '📺',
    'Pantalla Digital':  '🖥️',
    'Puente':            '🌉',
    'Señal Vial':        '⚠️',
    'Mobiliario Urbano': '🏙️',
  };

  const getMarkerIcon = (space) => {
    if (!window.google) return null;

    // Color de borde/fondo por estado
    let color = '#0057B7';
    if (space.Estado === 'Disponible')       color = '#28a745';
    else if (space.Estado === 'Ocupada')     color = '#dc3545';
    else if (space.Estado === 'Reservada')   color = '#ffc107';
    else if (space.Estado === 'En Mantenimiento') color = '#6c757d';

    const emoji = TIPO_EMOJI[space.Tipo_Espacio] || '📍';

    // SVG circular con emoji dentro, coloreado por estado
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="50" viewBox="0 0 44 50">
      <!-- Pin shadow -->
      <ellipse cx="22" cy="48" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
      <!-- Pin body -->
      <path d="M22 2 C11.5 2 3 10.5 3 21 C3 33 22 47 22 47 C22 47 41 33 41 21 C41 10.5 32.5 2 22 2 Z"
            fill="${color}" stroke="white" stroke-width="2.5"/>
      <!-- Emoji text -->
      <text x="22" y="26" text-anchor="middle" dominant-baseline="middle"
            font-size="17" font-family="Segoe UI Emoji, Apple Color Emoji, Noto Color Emoji, sans-serif">
        ${emoji}
      </text>
    </svg>`;

    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(44, 50),
      anchor: new window.google.maps.Point(22, 50),
    };
  };

  if (loadError) return <Box sx={{ p: 2, textAlign: 'center' }}><Typography color="error">Error al cargar el mapa: {loadError.message}</Typography></Box>;
  if (!isLoaded || (!mapsApiKeyProp && !googleMapsApiKey)) return <Box sx={{ p: 2, textAlign: 'center' }}><Typography>Cargando mapa...</Typography></Box>;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={8}
        options={mapOptions}
        onLoad={onMapLoad}
        onIdle={handleIdle}
      >
        <MarkerClustererF>
          {(clusterer) =>
            espacios.map((space) => (
              <MarkerF
                key={space.Codigo}
                position={{ lat: parseFloat(space.Latitud), lng: parseFloat(space.Longitud) }}
                onClick={() => handleMarkerClick(space)}
                onMouseOver={() => setHoveredSpace(space)}
                onMouseOut={() => setHoveredSpace(null)}
                clusterer={clusterer}
                icon={getMarkerIcon(space)}
              />
            ))
          }
        </MarkerClustererF>

        {selectedSpaceFromApp && ( // InfoWindow se muestra si hay un espacio seleccionado
          <InfoWindowF
            position={{ lat: parseFloat(selectedSpaceFromApp.Latitud), lng: parseFloat(selectedSpaceFromApp.Longitud) }}
            onCloseClick={handleInfoWindowClose}
          >
            <Box sx={{ p: 1, minWidth: '220px' }}>
              <Paper elevation={0} sx={{ mb: 1.5, p: 0.5, bgcolor: 'secondary.light', display: 'flex', justifyContent: 'center' }}>
                <Button 
                  size="small" 
                  variant="contained" 
                  color="primary"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToCart(selectedSpaceFromApp);
                  }}
                  sx={{ fontWeight: 'bold' }}
                >
                  {cart.find(i => i.Codigo === selectedSpaceFromApp.Codigo) ? "QUITAR DE MI INTERÉS" : "ME INTERESA"}
                </Button>
              </Paper>

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Valla Seleccionada
                <Chip label={selectedSpaceFromApp.Estado} size="small" color={selectedSpaceFromApp.Estado === 'Disponible' ? 'success' : 'warning'} sx={{ height: 20, fontSize: '0.65rem' }} />
              </Typography>
              {selectedSpaceFromApp.url_foto && (
                <Box sx={{ mt: 1, mb: 1, width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <img 
                    src={selectedSpaceFromApp.url_foto} 
                    alt={`Foto de ${selectedSpaceFromApp.Codigo}`} 
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }} 
                    onError={(e) => { e.target.onerror = null; e.target.src="/placeholder-image.png"; }}
                  />
                </Box>
              )}
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <span style={{ fontWeight: 'bold' }}>Tipo:</span> {selectedSpaceFromApp.Tipo_Espacio}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <span style={{ fontWeight: 'bold' }}>Tamaño:</span> {selectedSpaceFromApp.Tamano}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <span style={{ fontWeight: 'bold' }}>Zona:</span> {selectedSpaceFromApp.Zona}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <span style={{ fontWeight: 'bold' }}>Provincia:</span> {selectedSpaceFromApp.Provincia}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <span style={{ fontWeight: 'bold' }}>Dirección:</span> {selectedSpaceFromApp.Direccion}
              </Typography>
              <Link 
                href={`https://www.google.com/maps/search/?api=1&query=${selectedSpaceFromApp.Latitud},${selectedSpaceFromApp.Longitud}`} 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ mt: 1, display: 'block' }}
              >
                Ver en Google Maps
              </Link>
            </Box>
          </InfoWindowF>
        )}
      </GoogleMap>

      {/* Tarjeta de detalles flotante al hacer HOVER */}
      {hoveredSpace && (
        <FloatingDetailsCard>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Valla Disponible
          </Typography>
          {hoveredSpace.url_foto && (
            <Box sx={{ mt: 1, mb: 1, width: '100%', display: 'flex', justifyContent: 'center' }}>
              <img 
                src={hoveredSpace.url_foto} 
                alt={`Foto de ${hoveredSpace.Codigo}`} 
                style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
                onError={(e) => { e.target.onerror = null; e.target.src="/placeholder-image.png"; }}
              />
            </Box>
          )}
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <span style={{ fontWeight: 'bold' }}>Tipo:</span> {hoveredSpace.Tipo_Espacio}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <span style={{ fontWeight: 'bold' }}>Tamaño:</span> {hoveredSpace.Tamano}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <span style={{ fontWeight: 'bold' }}>Zona:</span> {hoveredSpace.Zona}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <span style={{ fontWeight: 'bold' }}>Provincia:</span> {hoveredSpace.Provincia}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <span style={{ fontWeight: 'bold' }}>Dirección:</span> {hoveredSpace.Direccion}
          </Typography>
        </FloatingDetailsCard>
      )}

      {/* Leyenda de tipos de espacio - Movida a la DERECHA para evitar solapamiento */}
      <Box sx={{
        position: 'absolute',
        bottom: 25,
        right: 55, // Evita los controles de zoom usuales
        backgroundColor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(4px)',
        borderRadius: 2,
        boxShadow: 3,
        px: 1.5,
        py: 1,
        zIndex: 1000,
        minWidth: 140,
      }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: 'text.secondary' }}>
          Tipos de espacio
        </Typography>
        {[
          { tipo: 'Valla',             emoji: '🪧' },
          { tipo: 'Valla Digital',     emoji: '📺' },
          { tipo: 'Pantalla Digital',  emoji: '🖥️' },
          { tipo: 'Puente',            emoji: '🌉' },
          { tipo: 'Señal Vial',        emoji: '⚠️' },
          { tipo: 'Mobiliario Urbano', emoji: '🏙️' },
        ].map(({ tipo, emoji }) => (
          <Box key={tipo} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
            <span style={{ fontSize: 14 }}>{emoji}</span>
            <Typography variant="caption" sx={{ color: 'text.primary', lineHeight: 1.4 }}>{tipo}</Typography>
          </Box>
        ))}
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 0.8, pt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.3, color: 'text.secondary' }}>
            Estado
          </Typography>
          {[
            { label: 'Disponible',       color: '#28a745' },
            { label: 'Ocupada',          color: '#dc3545' },
            { label: 'Reservada',        color: '#ffc107' },
            { label: 'En Mantenimiento', color: '#6c757d' },
          ].map(({ label, color }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: 'text.primary', lineHeight: 1.4 }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default PublicMapComponent;